import type { Glyph } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, fromSegment, type HeadPicture, inEllipse, mask, type SetFlair } from './mythic';

/**
 * The Glitch set (ADR-0160 amended, mythic): a digital ghost, the one sci-fi set in the garden. A visor helmet whose
 * screen face changes, white techwear with neon seams, and the Overclock gun.
 *
 * The head is a sleek black helmet (a mask over the whole head): glossy, a hard sheen on its crown, lit along its
 * top-left rim, a magenta bounce along its shadowed one, a pod at its side with an antenna, and a wide visor of black
 * glass in a lit bezel. The visor is a screen: its face is the head's flair (a `glyph`, a cycle of pixel pictures in the
 * team's light with white hot pixels, over a glow) that changes every second or three: o o, ^ ^, > <, a content ‿ ‿,
 * X X, hearts, a "?"; it blinks, and a refresh line of the team's colour sweeps down it. The picture carries the face
 * dim (o o, standby) for stills, and X X brighter lying. The suit is white techwear: a high collar, padded shoulders
 * piped in lime neon, a chest rig of straps, the team's light in an LED on the chest and in the belt, white sneakers.
 * Its flair is a signal breaking up: every 2-3.5 s, for a fifth of a second, the glitch (bars of magenta, lime and white
 * tearing sideways off the helmet and the jacket at once, the visor's face torn, a few pixels falling), quiet between;
 * pixels crumbling off it now and then, a holo-drone circling the head, the antenna's light blinking, a trail of dead
 * pixels hanging where it ran.
 *
 * Rubric (`assetc mythic glitch` at 5× and 8× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside
 * Inferno's; its bursts caught at 1:1 and 3× by a probe of the same drawing): helmet S5 R5 P4 L4 C4 A5 F4, techwear S3
 * R4 P4 L4 C4 F4. Still weak: the shell is black, so at night it leans on its rim and its face (which is always lit);
 * the red team's pink face is dimmer than the blue's cyan; the jacket shows little past the helmet and the gun (its
 * shoulder pad and collar carry it). The face's glyph rides the body's squash by its top-left only (its px don't
 * stretch), so in a hard squash its far eye slides ~3 px over the dim still face for a moment.
 */

// --- The visor helmet -----------------------------------------------------------------------------------------------

/** From the head's middle (u forward, v down): the helmet (a rounded square: a superellipse) and its visor. */
const HELM = { u: 1, v: -1.5, a: 22, b: 18.5, p: 2.4 } as const;
/** Its jaw: flatter and shorter under its middle, so the collar shows under it. */
const JAW = { ...HELM, b: 17.5, p: 3.2 } as const;
const HELM_E: Ellipse = [HELM.u, HELM.v, HELM.a, HELM.b];
const VISOR = { u: 3.5, v: 1.5, a: 16, b: 7.5, p: 4 } as const;
/** The pod on its side (u, v, r) and the antenna rising out of it (from, to). */
const POD = [-17, 3.5, 4.3] as const;
const ANTENNA = [-18.5, -1, -21.5, -22] as const;

const superAt = (u: number, v: number, s: { u: number; v: number; a: number; b: number; p: number }) =>
  Math.abs((u - s.u) / s.a) ** s.p + Math.abs((v - s.v) / s.b) ** s.p;

/** The head's frame px from (u, v) (`standing` samples the middle of each). */
const px = (u: number, v: number): [number, number] => [Math.floor(u + 30), Math.floor(v + 28)];

/**
 * The screen's faces, in the frame's px from `FACE_AT` (its top-left): `c` the team's light, `w` its shine, `p` and `l`
 * the glitch's magenta and lime. Each eye is in its own box (the near one at x 24-29, the far one at x 37-42).
 */
const FACE_AT = [23, 24] as const;
const FACE_W = 21;
const FACE_H = 9;

/** Two eyes from their own grids, the near one's top-left at column 1, the far one's at 14, `dy` rows down. */
function pair(near: string, far: string, dy = 1, farDx = 14): string[] {
  const rows = Array.from({ length: FACE_H }, () => Array(FACE_W).fill('.') as string[]);
  for (const [src, x0] of [
    [near, 1],
    [far, farDx],
  ] as const) {
    const lines = src
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length);
    lines.forEach((line, y) => {
      [...line].forEach((ch, x) => {
        if (ch !== '.' && rows[y + dy]) (rows[y + dy] as string[])[x + x0] = ch;
      });
    });
  }
  return rows.map((r) => r.join(''));
}

/** A whole face drawn as one grid (its rows padded to the face's size). */
const whole = (src: string): string[] => {
  const lines = src
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length);
  return Array.from({ length: FACE_H }, (_, y) => (lines[y] ?? '').padEnd(FACE_W, '.'));
};

const EYE = `
  .ccc.
  ccccc
  cwccc
  cwccc
  ccccc
  ccccc
  .ccc.
`;
const NEUTRAL = pair(EYE, EYE);
const HAPPY = pair(
  `
  ..ww..
  .cccc.
  cc..cc
  c....c
`,
  `
  ..ww..
  .cccc.
  cc..cc
  c....c
`,
  2,
);
const SQUINT = pair(
  `
  cc....
  .ccc..
  ...ccw
  .ccc..
  cc....
`,
  `
  ....cc
  ..ccc.
  wcc...
  ..ccc.
  ....cc
`,
  2,
);
const CONTENT = pair(
  `
  c....c
  cc..cc
  .cwwc.
`,
  `
  c....c
  cc..cc
  .cwwc.
`,
  4,
);
const DEAD = pair(
  `
  cc...cc
  .cc.cc.
  ..cwc..
  .cc.cc.
  cc...cc
`,
  `
  cc...cc
  .cc.cc.
  ..cwc..
  .cc.cc.
  cc...cc
`,
  2,
  13,
);
const LOVE = pair(
  `
  .cc.cc.
  cwccccc
  ccccccc
  .ccccc.
  ..ccc..
  ...c...
`,
  `
  .cc.cc.
  cwccccc
  ccccccc
  .ccccc.
  ..ccc..
  ...c...
`,
  1,
  13,
);
const HUH = whole(`
  .....................
  ........cccc.........
  .......cc..cc........
  ...........cc........
  ..........cc.........
  .........cc..........
  .....................
  .........ww..........
`);
/** The faces in the order the screen may show them: the plain ones twice as often. */
const FACES = [NEUTRAL, HAPPY, NEUTRAL, SQUINT, HAPPY, CONTENT, DEAD, LOVE, HUH];

/** The plain face torn: each row slid by its shift, its light split (magenta behind a slid run, lime ahead of it). */
function tornFace(shifts: readonly number[]): string[] {
  return NEUTRAL.map((row, y) => {
    const s = shifts[y] ?? 0;
    if (!s) return row;
    const out = Array<string>(FACE_W).fill('.');
    const lit = [...row].flatMap((ch, x) => (ch === '.' ? [] : [x]));
    for (const x of lit) {
      if (x + s - 1 >= 0 && x + s - 1 < FACE_W) out[x + s - 1] = 'p';
      if (x + s + 1 < FACE_W) out[x + s + 1] = 'l';
    }
    for (const x of lit) if (x + s >= 0 && x + s < FACE_W) out[x + s] = row[x] as string;
    return out.join('');
  });
}
/** A blink: every face's light dark for a moment but a line across each eye. */
const BLINK_LINE = pair('cccccc', 'cccccc', 4);

/** The static face (o o standing, X X lying) at frame px (x, y): its key, or null. */
function faceAt(face: readonly string[], x: number, y: number): string | null {
  const ch = face[y - FACE_AT[1]]?.[x - FACE_AT[0]];
  return ch === undefined || ch === '.' ? null : ch;
}

/**
 * A screen face as a glyph frame over the picture: its light, and the visor's own black (`k`) wherever the picture's
 * still face (o o) lit a px that this one doesn't, so the two never show at once.
 */
const frameOf = (face: readonly string[]): string =>
  face
    .map((row, y) =>
      [...row]
        .map((ch, x) => (ch !== '.' ? ch : NEUTRAL[y]?.[x] !== '.' && NEUTRAL[y]?.[x] !== undefined ? 'k' : '.'))
        .join(''),
    )
    .join('\n');

/** The blink: black over every px a face can light, but the line. */
const blinkFrame = (): string =>
  Array.from({ length: FACE_H }, (_, y) =>
    Array.from({ length: FACE_W }, (_, x) => {
      if (BLINK_LINE[y]?.[x] === 'c') return 'c';
      return FACES.some((f) => (f[y]?.[x] ?? '.') !== '.') ? 'k' : '.';
    }).join(''),
  ).join('\n');

const helmet: HeadPicture = (u, v, lx, ly, lyingDown) => {
  // Lying, the face is X X.
  const [fx, fy] = px(u, v);
  const s = superAt(u, v, v > HELM.v ? JAW : HELM);
  // The antenna and its tip.
  if (fromSegment(u, v, ...ANTENNA) < 1.05 && v < 0) {
    if (v < ANTENNA[3] + 2.6) return Math.hypot(u - ANTENNA[2], v - ANTENNA[3]) < 1.2 ? 'w' : 'l';
    return u - (ANTENNA[0] + ((ANTENNA[2] - ANTENNA[0]) * (v - ANTENNA[1])) / (ANTENNA[3] - ANTENNA[1])) < 0
      ? '3'
      : '2';
  }
  // The pod on its side: a ring of steel round a lime light.
  const pod = Math.hypot(u - POD[0], v - POD[1]);
  if (pod < POD[2] && s < 1.06) {
    if (pod < 1.6) return 'l';
    if (pod < 2.6) return 'G';
    return (u - POD[0]) * lx + (v - POD[1]) * ly > 0.5 ? '4' : pod > 3.6 ? '1' : '2';
  }
  // Lying, its neck ring shows past its jaw (seen from above, toward the shoulders), over the face under it.
  if (s > 1 && lyingDown && v > 0 && v < 23.5 && Math.abs(u - 2) < 15 - (v - 15) * 0.4)
    return v > 21.5 ? '1' : Math.abs(v - 19) < 0.6 ? 'l' : u < -4 ? '3' : '2';
  if (s > 1) return null;
  // The visor: black glass in a bezel, a shine across its top-left, the screen's face on it.
  const vz = superAt(u, v, VISOR);
  if (vz <= 1) {
    const face = faceAt(lyingDown ? DEAD : NEUTRAL, fx, fy);
    if (face) return lyingDown ? (face === 'w' ? 'c' : 'b') : face === 'w' ? 'b' : 'B';
    const [du, dv] = [u - VISOR.u, v - VISOR.v];
    if (dv < -5.6) return du < 2 ? '2' : '1';
    // A glare, two strokes slanting across its top-left corner.
    const glare = du + dv * 1.2;
    if (dv < -2.5 && du < -9 && ((glare > -18.5 && glare < -17) || (glare > -15.5 && glare < -14.6))) return '2';
    return 'k';
  }
  // Its bezel: lit where it faces the light, shaded under.
  if (vz <= 1.45) {
    const f = (u - VISOR.u) * lx * 0.5 + (v - VISOR.v) * ly;
    return f > 4 ? '4' : f > -2.5 ? '3' : '2';
  }
  // The shell: glossy black, a hard sheen on its crown, lit along the rim it shows the light.
  const l = domeLight(u, v, HELM_E, lx, ly);
  const [nu, nv] = [(u - HELM.u) / HELM.a, (v - HELM.v) / HELM.b];
  const facing = (nu * lx + nv * ly) / (Math.hypot(nu, nv) || 1);
  if (s > 0.82) {
    if (facing > 0.55) return s > 0.93 ? '5' : '4';
    if (facing > 0.1) return '3';
    if (facing < -0.45 && s > 0.9) return 'P';
  }
  if (l > 0.975) return 'w';
  if (l > 0.93) return '4';
  if (l > 0.78) return '2';
  if (l > 0.2) return '1';
  return 'k';
};

// --- White techwear --------------------------------------------------------------------------------------------------

/**
 * The jacket standing (the frame's x 5-56, y 39-55): white, lit along its top-left, a high collar, padded shoulders
 * piped in lime, a rig of black straps across the chest, the team's light in an LED on the near breast and in the belt.
 */
function techwear(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 17);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  for (let y = 44; y <= 52; y++)
    for (let x = 16; x <= 43; x++) {
      const edge = x === 16 || y === 44;
      at(x, y, edge ? 'w' : x <= 18 || y === 45 ? 'w' : x >= 40 || y >= 51 ? '4' : '5');
    }
  // The high collar.
  for (let y = 41; y <= 45; y++)
    for (let x = 21; x <= 39; x++) at(x, y, y === 41 ? 'w' : x < 24 ? '5' : x > 36 ? '4' : y > 43 ? '3' : '5');
  // The shoulders: padded, piped in lime along their foot.
  for (const [cx, rx] of [
    [12.5, 6.8],
    [47, 6],
  ] as const) {
    const e: Ellipse = [cx, 47.5, rx, 5.2];
    for (let y = 41; y <= 54; y++)
      for (let x = 5; x <= 56; x++) {
        const r = inEllipse(x + 0.5, y + 0.5, e);
        if (r > 1) continue;
        const l = -(x + 0.5 - cx) / rx - (y + 0.5 - 47.5) / 5.2;
        const foot = y + 0.5 - 47.5 > 1.5 && r > 0.55 && r < 0.85;
        at(x, y, foot ? 'l' : r > 0.85 && l < -0.3 ? '3' : l > 0.6 ? 'w' : l > -0.5 ? '5' : '4');
      }
  }
  // The rig: two straps from the shoulders, crossing low on the chest.
  for (const [xa, xb] of [
    [17, 33],
    [43, 27],
  ] as const)
    for (let k = 0; k <= 9; k++) {
      const x = Math.round(xa + ((xb - xa) * k) / 9);
      const y = 44 + k;
      at(x, y, '3');
      at(x + 1, y, '2');
      at(x + 2, y, '1');
    }
  // Seams of lime down the jacket's front panels.
  for (let y = 46; y <= 52; y++) {
    at(20, y, 'l');
    at(39, y, 'g');
  }
  // The chest LED in the team's light.
  c.stamp(
    `
    .BB.
    BccB
    BcwB
    .BB.
  `,
    22 - x0,
    46 - y0,
  );
  // The belt in the team's colour, a white buckle.
  for (let x = 16; x <= 43; x++) {
    at(x, 53, 'c');
    at(x, 54, 'b');
    at(x, 55, 'B');
  }
  c.stamp(
    `
    w55
    5ww
    543
  `,
    29 - x0,
    53 - y0,
  );
  return [c, x0, y0];
}

/** The jacket lying (right of the legs, under the head): the belt at the waist (left), a strap, the LED, a seam. */
const DEAD_TECH = grid(`
  ....5544
  ...55w44
  c35wwl55
  b3wwlw55
  B35wl555
  c3w2l555
  b35w2555
  B3555255
  c3555525
  b355BB52
  B35BccB5
  c35BcwB5
  b355BB55
  B3l55555
  c3l5555.
  b3l55555
  B3344444
  ....3333
`);

/** White sneakers under the team's trousers. */
const TECHWEAR = (): Outfit => ({ torso: techwear(), dead: [DEAD_TECH, 24, 51], legs: { n: '5', N: '4' } });

// --- Overclock on every other gun ------------------------------------------------------------------------------------

/**
 * The Overclock finish (the default gun is its own, `pollinator_glitch`): matte black where the steel and the wood were,
 * its glints dull slate, lime neon where the brass was.
 */
const OVERCLOCK = {
  '1': 'k',
  '2': 'k',
  '3': '1',
  '4': '2',
  '5': '3',
  N: 'k',
  n: 'k',
  m: '1',
  t: '2',
  e: '3',
  U: 'G',
  Y: 'l',
  y: 'w',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const MAGENTA = 0xb55088;
const LIME = 0x63c74d;
const GREEN = 0x3e8948;
const VISOR_BLACK = 0x181425;

/** The screen's face: one of `FACES` at a time, held a second or three. */
const SCREEN: Glyph = {
  kind: 'glyph',
  at: { on: 'head', x: FACE_AT[0], y: FACE_AT[1] },
  frames: FACES.map(frameOf),
  tones: { c: 'teamLight', w: WHITE, p: MAGENTA, l: LIME, k: VISOR_BLACK },
  hold: [1.2, 3],
  order: 'random',
  glow: [14, 0.36],
  layer: 'front',
};

/** Its blink, over whatever face it shows. */
const BLINK: Glyph = {
  kind: 'glyph',
  at: { on: 'head', x: FACE_AT[0], y: FACE_AT[1] },
  frames: [blinkFrame()],
  tones: { c: 'teamLight', k: VISOR_BLACK },
  hold: [2.6, 4.8],
  shown: 0.035,
  layer: 'front',
};

/**
 * The screen's refresh: a line of the team's colour sweeping down the visor (added over the glass and the face), a
 * frame a row, then a while dark (frames of nothing): a sweep about every 1.6 s.
 */
const SWEEP_TOP = 23;
const SWEEP: Glyph = {
  kind: 'glyph',
  at: { on: 'head', x: 18, y: SWEEP_TOP },
  frames: [
    ...Array.from({ length: 14 }, (_, row) => {
      // The glass's width on this row (its rounded corners), from the visor's shape.
      const v = SWEEP_TOP + row + 0.5 - 28;
      const cols = Array.from({ length: 32 }, (_, x) => (superAt(18 + x + 0.5 - 30, v, VISOR) <= 1 ? 'c' : '.'));
      return `${'.\n'.repeat(row)}${cols.join('')}`;
    }),
    ...Array<string>(40).fill('.'),
  ],
  tones: { c: 'team' },
  hold: [0.03, 0.03],
  order: 'cycle',
  add: true,
  layer: 'front',
};

/**
 * The glitch: every 2-3.5 s, for a fifth of a second, the signal tears. Bars of magenta, lime and white rip sideways off
 * the helmet and the jacket at once, the visor's face is torn, a few pixels fall; then nothing till the next. One glyph
 * (so it all goes at once) on the head, its frames in turn a twentieth of a second each, runs of nothing between bursts.
 * A frame's marks are [x, y, w, h, key] in the body's frame px; `torn` puts a torn face on the visor over whatever face
 * it shows.
 */
type Mark = readonly [x: number, y: number, w: number, h: number, key: string];
const BURST_AT = [0, 8] as const;
const BURST_W = 68;
const BURST_H = 54;

function burstFrame(marks: readonly Mark[], torn?: readonly string[]): string {
  const rows = Array.from({ length: BURST_H }, () => Array<string>(BURST_W).fill('.'));
  const put = (x: number, y: number, key: string) => {
    const row = rows[y - BURST_AT[1]];
    if (row && x >= BURST_AT[0] && x - BURST_AT[0] < BURST_W) row[x - BURST_AT[0]] = key;
  };
  if (torn)
    for (let y = 0; y < FACE_H; y++)
      for (let x = 0; x < FACE_W; x++) {
        const lit = torn[y]?.[x] ?? '.';
        if (lit !== '.') put(FACE_AT[0] + x, FACE_AT[1] + y, lit);
        else if (FACES.some((f) => (f[y]?.[x] ?? '.') !== '.')) put(FACE_AT[0] + x, FACE_AT[1] + y, 'k');
      }
  for (const [x, y, w, h, key] of marks) for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) put(x + i, y + j, key);
  return rows.map((row) => row.join('')).join('\n');
}

/** Off the helmet's back and its visor's side, the jacket's shoulder and its front; a few pixels loose, falling. */
const RIP: readonly Mark[] = [
  [2, 15, 15, 2, 'w'],
  [5, 17, 15, 1, 'p'],
  [46, 33, 18, 2, 'l'],
  [43, 35, 15, 1, 'w'],
  [0, 46, 14, 2, 'p'],
  [3, 48, 8, 1, 'l'],
  [50, 55, 15, 2, 'w'],
  [1, 22, 1, 1, 'w'],
  [63, 26, 1, 1, 'l'],
  [59, 43, 1, 1, 'p'],
  [4, 53, 1, 1, 'w'],
];
/** The bars jumped elsewhere, further out; the loose pixels lower. */
const SHIFT: readonly Mark[] = [
  [0, 20, 13, 2, 'l'],
  [3, 22, 12, 1, 'w'],
  [44, 12, 17, 2, 'p'],
  [47, 14, 10, 1, 'w'],
  [52, 30, 15, 2, 'w'],
  [0, 50, 10, 2, 'w'],
  [2, 52, 6, 1, 'p'],
  [55, 46, 12, 2, 'l'],
  [1, 25, 1, 1, 'w'],
  [63, 30, 1, 1, 'l'],
  [59, 47, 1, 1, 'p'],
  [4, 57, 1, 1, 'w'],
];
/** The first bars again, torn further out. */
const RIP2: readonly Mark[] = RIP.map(([x, y, w, h, key]) => [
  x < 30 ? x - 2 : x + 2,
  y + (h === 1 && w === 1 ? 6 : 0),
  w,
  h,
  key,
]);
/** What is left of it: thin remnants, the last pixels. */
const FADE: readonly Mark[] = [
  [0, 18, 8, 1, 'p'],
  [58, 34, 9, 1, 'l'],
  [0, 49, 6, 1, 'w'],
  [1, 31, 1, 1, 'w'],
  [63, 36, 1, 1, 'l'],
  [59, 53, 1, 1, 'p'],
];
const TORN_A = tornFace([0, 0, 0, 3, 3, 0, -2, 0, 0]);
const TORN_B = tornFace([0, -2, -2, 0, 0, 2, 2, 0, 0]);
const [rip, shift, rip2, fade] = [
  burstFrame(RIP, TORN_A),
  burstFrame(SHIFT, TORN_B),
  burstFrame(RIP2, TORN_A),
  burstFrame(FADE),
];
const quiet = (frames: number) => Array<string>(frames).fill('.');
/** Its frames: four bursts, uneven gaps (2-3.5 s) between them. */
const BURST: Glyph = {
  kind: 'glyph',
  at: { on: 'head', x: BURST_AT[0], y: BURST_AT[1] },
  frames: [
    ...[rip, shift, rip2, fade],
    ...quiet(40),
    ...[shift, rip, fade],
    ...quiet(56),
    ...[rip, shift, rip2, fade],
    ...quiet(36),
    ...[shift, rip2, fade],
    ...quiet(62),
  ],
  tones: { c: 'teamLight', w: WHITE, p: MAGENTA, l: LIME, k: VISOR_BLACK },
  hold: [0.055, 0.055],
  order: 'cycle',
  layer: 'front',
};

/** Pixels crumbling off: born where they were, falling away, blinking out. */
const CRUMBS = [WHITE, WHITE, LIME, MAGENTA] as const;

const FLAIR: SetFlair = {
  head: [
    SCREEN,
    SWEEP,
    BLINK,
    BURST,
    // The antenna's light, blinking.
    {
      kind: 'glyph',
      at: { on: 'head', x: 8, y: 4 },
      frames: ['ww\nww'],
      tones: { w: LIME },
      hold: [0.8, 0.8],
      shown: 0.45,
      glow: [5, 0.5],
      layer: 'front',
    },
    // A holo-drone circling the head.
    {
      kind: 'orbit',
      at: { on: 'head', x: 30, y: 18 },
      rx: 27,
      ry: 6,
      tilt: -0.12,
      n: 1,
      period: 3.4,
      size: 4,
      tones: [WHITE, LIME, GREEN],
      tail: 0.12,
      glow: 4,
      bob: [1.5, 1.3],
    },
    // Pixels crumbling off its back.
    {
      kind: 'emit',
      at: { on: 'head', x: 12, y: 30 },
      box: [6, 26],
      rate: 2.5,
      v: [-14, -4, -8, 4],
      g: 60,
      life: [0.4, 0.8],
      size: [2, 1],
      tones: CRUMBS,
      layer: 'back',
    },
  ],
  outfit: [
    // Pixels crumbling off the jacket.
    {
      kind: 'emit',
      at: { on: 'torso', x: 14, y: 50 },
      box: [10, 10],
      rate: 2.5,
      v: [-12, -2, -6, 4],
      g: 60,
      life: [0.4, 0.8],
      size: [2, 1],
      tones: CRUMBS,
      layer: 'back',
    },
    // A trail of dead pixels on the run, hanging where they were left and blinking out.
    {
      kind: 'emit',
      at: { on: 'feet', x: 30, y: 64 },
      box: [16, 8],
      rate: 0,
      run: 0.07,
      v: [-3, 3, -4, 0],
      life: [0.35, 0.7],
      size: [2, 2],
      tones: [WHITE, LIME, MAGENTA],
      layer: 'back',
    },
  ],
  gun: [
    // The emitter's charge at the muzzle and sparks off the heat sink.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 62.5, y: 20 },
      r: [3, 5],
      alpha: [0.3, 0.55],
      period: 0.45,
      tone: LIME,
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 33, y: 9 },
      box: [12, 2],
      rate: 3,
      v: [-10, 10, -30, -12],
      g: 90,
      life: [0.25, 0.5],
      size: [1, 1],
      tones: [WHITE, LIME, MAGENTA],
      add: true,
      layer: 'gun',
    },
  ],
};

/** The Glitch set (ADR-0160 amended). */
export const GLITCH = drawnLater({ head: () => mask(helmet), outfit: TECHWEAR, gun: OVERCLOCK, flair: FLAIR });
